/**
 * Global Chart Configuration
 * This configuration can be loaded from a database in the future
 * Structure is designed to be JSON-serializable
 */

import { IStatMarkerLayoutConfig } from "src/pages/Insights/models/interfaces";

export interface ChartConfiguration {
	domain: DomainConfig;
	layout: LayoutConfig;
	dimensions: DimensionsConfig;
	typography: TypographyConfig;
	spacing: SpacingConfig;
	responsive: ResponsiveConfig;
	charts: ChartsConfig;
	tooltip: TooltipConfig;
	colors: ColorsConfig;
	multiTile: MultiTileScalingConfig;
}

interface DomainConfig {
	percentage: {
		maxThreshold: number; // 100.5 - threshold for standard 0-100% range
		bufferMultiplier: number; // 1.1 - 10% buffer for values exceeding 100%
		roundingDivisor: number; // 10 - rounding to nearest 10
	};
	numeric: {
		bufferMultiplier: number; // 1.1 - 10% buffer
		roundingDivisor: number; // 10
		minRoundValue: number; // 10 - threshold for small numbers
		intervalDivisor: number; // 4 - number of intervals
	};
}

interface LayoutConfig {
	header: {
		rowHeight: number; // 32 or 40 depending on chart type
		rowHeightStacked: number; // 40 for stacked charts
		rowHeightMultiTile: number; // 24
	};
	nSize: {
		rowHeight: number; // 16
	};
	axis: {
		width: number; // 45 for vertical charts
		top: number; // 20
		bottom: number; // 0
	};
	grid: {
		left: number;
		right: number;
		top: number;
		bottom: number;
	};
}

interface DimensionsConfig {
	column: {
		minWidth: number; // 120 - standard min width
		minWidthCompact: number; // 70 - compact mode min width
		minWidthUltraCompact: number; // 55 - for 1-2 bars per column
		minDataWidth: number; // 180 for stacked horizontal
		minAttrWidth: number; // 80
		maxAttrWidth: number; // 200
		minWaveWidth: number; // 60
		maxWaveWidth: number; // 120
		waveLabelWidth: number; // 60
		stackedMinWidth: number; // 120 for vertical stacked
		stackedMinWidthCompact: number; // 80 - compact mode for stacked
		compactThreshold: number; // 8 - columns count to trigger compact mode
		ultraCompactBarThreshold: number; // 2 - max bars per column for ultra-compact
	};
	row: {
		minHeight: number; // 42-48 depending on chart
		maxHeight: number; // 90
		minHeightClustered: number; // 48 for clustered
		minHeightStacked: number; // 42 for stacked
		labelHeight: number; // 12
		autoStretchThreshold: number; // 3 - rows below this get stretched
	};
	bar: {
		minHeight: number; // 24
		maxHeight: number; // 32
		maxWidth: number; // 50 for vertical bars
		defaultWidth: string; // "70%" for stacked
	};
	chart: {
		minHeight: number; // 250
		maxHeight: number; // 900
		defaultHeight: number; // 280-300
	};
}

interface TypographyConfig {
	charWidth: number; // 7 - approximate width per character
	fontSize: {
		label: number; // 10
		dataLabel: number; // 9-10 - for bar data labels
		dataLabelCompact: number; // 8 - for compact mode data labels
	};
}

interface SpacingConfig {
	gap: {
		bar: number; // 6
		row: number; // 6-12 depending on row count
		rowSmall: number; // 6 for many rows
		rowLarge: number; // 12 for few rows
	};
	padding: {
		cell: {
			vertical: number; // 8
			horizontal: number; // 4
		};
		container: number; // 24
		buffer: number; // 15
		safety: number; // 6-10
	};
	bias: {
		stretch: number; // 6
		zoom: number; // 8 for single row compression
		zoomGapRecovery: number;
	};
}

interface ResponsiveConfig {
	breakpoints: {
		mobile: number; // 768
		tablet: number; // 1024
	};
	scales: {
		mobile: number; // 0.7
		tablet: number; // 0.85
		desktop: number; // 1.0
	};
}

interface ChartsConfig {
	horizontalClustered: HorizontalClusteredConfig;
	horizontalStacked: HorizontalStackedConfig;
	verticalClustered: VerticalClusteredConfig;
	verticalStacked: VerticalStackedConfig;
	horizontalLine: HorizontalLineConfig;
	verticalLine: VerticalLineConfig;
	mini: MiniChartConfig;
}

interface HorizontalClusteredConfig {
	yAxisLabel: {
		minWidth: number; // 120
		maxWidth: number; // 220
		multiTileMinWidth: number; // ← NEW
		multiTileMaxWidth: number; // 70
	};
	rowThreshold: number; // 5 - threshold for gap adjustment
	diffThreshold: number; // 20 - threshold for bar height decision
	statMarkerLayout?: IStatMarkerLayoutConfig;
}

interface HorizontalStackedConfig {
	minLabelWidth: number; // 10 - minimum pixels to show label
	barWidthApprox: number; // 280 - approximate bar width for calculations
	barWidthMultiplier: number; // 0.35 - multiplier for rowHeight
	maxBarWidth: number; // 20
	showLabelThreshold: number;
	multiTileMinLabelWidth: number;
	multiTileMaxLabelWidth: number;
	statMarkerLayout?: IStatMarkerLayoutConfig;
}

interface VerticalClusteredConfig {
	clusterBarWidth: number; // 26
	clusterBarWidthCompact: number; // 18 - narrower bars for compact mode
	clusterBarWidthUltraCompact: number; // 16 - for 1-2 bars per column
	barGap: number; // 0 - no gap between bars in cluster
	maxBarWidth: number; // maximum width for bars
	maxBarWidthSingle: number; // maximum width for single bars
	minBarWidth: number; // minimum width for bars
	singleBarWidthPercent: number; // percent of column width for single bars
	statMarkerLayout?: IStatMarkerLayoutConfig;
}

interface VerticalStackedConfig {
	minColumnWidth: number; // 120
	minColumnWidthCompact: number; // 80 - compact mode
	barWidthPercent: number; // 0.8 - use 80% of column width for stacked bars
	minBarWidth: number; // 40 - minimum width for stacked bars
	maxBarWidth: number; // 80 - maximum width for stacked bars
	statMarkerLayout?: IStatMarkerLayoutConfig;
}

interface HorizontalLineConfig {
	minColumnWidth: number;
	minColumnWidthCompact: number;
	chartHeight: {
		min: number;
		max: number;
		default: number;
	};
	lineWeight: number;
	markerSize: number;
	labelTopPadding: number;
	attributesRowHeight: number;
	axisTop: number;
	axisBottom: number;
	statMarkerLayout?: IStatMarkerLayoutConfig;
}

interface VerticalLineConfig {
	minRowHeight: number;
	minRowHeightCompact: number;
	chartWidth: {
		min: number;
		max: number;
		default: number;
	};
	lineWeight: number;
	markerSize: number;
	labelRightPadding: number;
	attributesColWidth: number;
	multiTileMinLabelWidth: number;
	multiTileMaxLabelWidth: number;
	axisRight: number;
	axisLeft: number;
	xAxisScaleRight: number;
	statMarkerLayout?: IStatMarkerLayoutConfig;
	yAxisLabel: {
		minWidth: number; // 120
		maxWidth: number; // 220
		multiTileMaxWidth: number; // 80
	};
}

interface MiniChartConfig {
	horizontal: {
		barGapSingle: string; // "30%"
		barGapMultiple: string; // "0%"
		barCategoryGapSingle: string; // "40%"
		barCategoryGapMultiple: string; // "0%"
	};
	vertical: {
		barWidth: string; // "70%"
		barMaxWidth: number; // 50
	};
	statMarkerLayout?: IStatMarkerLayoutConfig;
}

interface TooltipConfig {
	backgroundColor: string;
	borderColor: string;
	borderWidth: number;
	borderRadius: string;
	fontSize: number;
	lowNSizeThreshold: number;
	lowNSizeColor: string;
	textColor: string;
	zIndex: number;
	boxShadow: string;
	displayTransition: boolean;
	transitionDuration: number;
}

interface ColorsConfig {
	borders: {
		solid: string;
		dashed: string;
		gray: string;
		lightGray: string;
	};
	backgrounds: {
		white: string;
		alternate: string;
	};
	text: {
		primary: string;
		secondary: string;
		tertiary: string;
		quaternary: string;
		muted: string;
	};
}

// Add this interface after the existing interfaces
interface IStatMarkerConfig {
	enabled: boolean;
	fontSize: number;
	fontWeight: number;
	color: string; // default color, overridden by segment color if useSegmentColor is true
	useSegmentColor: boolean;
	position: "top" | "bottom" | "inside";
	distance: number; // distance from bar edge
	gridTopPadding: number; // extra grid.top when markers are present
	minSegmentWidth: number; // minimum segment pixel width to show marker
	showOnReference: boolean; // true = markers on reference item, false = on non-reference
	applicationMethod: "compare_all" | "reference_based";
}

interface MultiTileScalingConfig {
	/**
	 * Reference dimensions — what a single chart was designed for.
	 * scaleFactor = sqrt((tileW / referenceWidth) × (tileH / referenceHeight))
	 */
	reference: {
		width: number; // 760
		height: number; // 550
	};

	/** Tile smaller than either threshold → render TooSmallFallback */
	minTileSize: {
		width: number; // 200
		height: number; // 160
	};

	/**
	 * Hard pixel floors — scaled values never go below these regardless
	 * of how small the tile gets.
	 */
	floors: {
		fontPx: number; // 10
		yAxisLabelFontPx: number;
		labelFontPx: number; // 8
		labelFontPxTbl: number;
		strokePx: number; // 1
		barPx: number; // 8
		vStackedBarPx: number;
		axisColumnPx: number; // 28
		containerPadding: number; // 4
		headerRowHeight: number; // 20
		nSizeRowHeight: number; // 12
		titlePx: number;
		legendFontPx: number;
	};

	/**
	 * scaleFactor thresholds that control progressive disclosure.
	 * Each value is the MINIMUM scaleFactor required to SHOW that element.
	 * e.g. showSubtitle: 0.58 → hidden when scaleFactor < 0.58
	 */
	visibilityThresholds: {
		subtitle: number; // 0.58
		axisLabels: number; // 0.54
		viewToggle: number; // 0.44
		legend: number; // 0.32
	};

	/**
	 * scaleFactor thresholds for stepped/discrete layout decisions.
	 */
	steppedThresholds: {
		/**
		 * maxAttrLabelLines steps:
		 *   scaleFactor > high  → 3 lines
		 *   scaleFactor > low   → 2 lines
		 *   else                → 1 line
		 */
		attrLabelLines: { high: number; low: number }; // { high: 0.62, low: 0.46 }
		/**
		 * legendLines steps:
		 *   scaleFactor > threshold → 2 lines, else → 1 line
		 */
		legendLines: { threshold: number }; // { threshold: 0.6 }
	};

	/**
	 * Base values for all scaled properties at scaleFactor = 1.
	 * These MUST match the single-chart chartConfig values exactly
	 * so that IDENTITY_SCALE (scaleFactor=1) is byte-identical to
	 * the old single-chart output.
	 */
	base: {
		typography: {
			title: number; // 12
			axisLabel: number; // 11  ← matches typography.fontSize.label (≈11px rendered)
			axisLabelTbl: number;
			yAxisLabel: number;
			dataLabel: number; // 9   ← matches typography.fontSize.dataLabel
			dataLabelTbl: number;
			legend: number; // 11
			nSize: number; // 11
			subtitle: number; // 10
		};
		geometry: {
			maxBarWidth: number; // 50  ← dimensions.bar.maxWidth
			maxVStackedBarWidth: number;
			strokeWidth: number; // 1.5
			markerRadius: number; // 4
			gridTop: number; // 20  ← layout.axis.top
			cellPaddingV: number; // 4   ← spacing.padding.cell.vertical
			cellPaddingH: number; // 4   ← spacing.padding.cell.horizontal
			legendSpacing: number; // 12
			axisColumnWidth: number; // 45  ← layout.axis.width
		};
		layout: {
			containerPadding: number; // 16  ← Tailwind p-4 used by ChartContainer
			headerRowHeight: number; // 32  ← layout.header.rowHeight
			nSizeRowHeight: number; // 16  ← layout.nSize.rowHeight
			maxAttrLabelLines: number; // 3
			legendLines: number; // 2
		};
	};
}

export interface YAxisWidthConfig {
	/** Absolute floor — never go below this regardless of container size */
	floorPx: number; // 40
	/** Maximum fraction of container width the label column may consume */
	maxContainerFraction: number; // 0.35 (single chart), 0.30 (multi-tile)
	/** Padding added to measured text width (accounts for cell padding + safety) */
	paddingPx: number; // 32 (single), 12 (multi-tile compact)
	/** If measured text is below this, snap to this minimum content width */
	minContentPx: number; // 60
	/** Hard ceiling in px — prevents absurdly wide columns on large screens */
	ceilingPx: number; // 220 (single), 120 (multi-tile)
	/** Multiplier applied to the ideal width before clamping (1.0 = no change) */
	expansionFactor?: number;
}

export const multiTileScalingDefaults: MultiTileScalingConfig = {
	reference: {
		width: 760,
		height: 550,
	},

	minTileSize: {
		width: 200,
		height: 160,
	},

	floors: {
		fontPx: 9,
		yAxisLabelFontPx: 10,
		labelFontPx: 9,
		labelFontPxTbl: 10,
		strokePx: 1,
		barPx: 23,
		vStackedBarPx: 37,
		axisColumnPx: 28,
		containerPadding: 4,
		headerRowHeight: 20,
		nSizeRowHeight: 16,
		titlePx: 13.5,
		legendFontPx: 11,
	},

	visibilityThresholds: {
		subtitle: 0.58,
		axisLabels: 0.54,
		viewToggle: 0.44,
		legend: 0.32,
	},

	steppedThresholds: {
		attrLabelLines: { high: 0.62, low: 0.46 },
		legendLines: { threshold: 0.6 },
	},

	base: {
		typography: {
			title: 13.5,
			axisLabel: 11,
			axisLabelTbl: 11,
			yAxisLabel: 11,
			dataLabel: 10,
			dataLabelTbl: 10,
			legend: 11,
			nSize: 11,
			subtitle: 10,
		},
		geometry: {
			maxBarWidth: 40,
			maxVStackedBarWidth: 50,
			strokeWidth: 1.5,
			markerRadius: 4,
			gridTop: 20,
			cellPaddingV: 4,
			cellPaddingH: 4,
			legendSpacing: 12,
			axisColumnWidth: 45,
		},
		layout: {
			containerPadding: 8,
			headerRowHeight: 32,
			nSizeRowHeight: 16,
			maxAttrLabelLines: 3,
			legendLines: 2,
		},
	},
};

/**
 * Default configuration - can be replaced with database values
 */
export const defaultChartConfig: ChartConfiguration = {
	domain: {
		percentage: {
			maxThreshold: 100.5,
			bufferMultiplier: 1.1,
			roundingDivisor: 10,
		},
		numeric: {
			bufferMultiplier: 1.05,
			roundingDivisor: 10,
			minRoundValue: 10,
			intervalDivisor: 4,
		},
	},
	layout: {
		header: {
			rowHeight: 32,
			rowHeightStacked: 40,
			rowHeightMultiTile: 24,
		},
		nSize: {
			rowHeight: 16,
		},
		axis: {
			width: 45,
			top: 20,
			bottom: 0,
		},
		grid: {
			left: -10,
			right: 36,
			top: 15,
			bottom: 0,
		},
	},
	dimensions: {
		column: {
			minWidth: 120,
			minWidthCompact: 60,
			minWidthUltraCompact: 48,
			minDataWidth: 180,
			minAttrWidth: 80,
			maxAttrWidth: 200,
			minWaveWidth: 60,
			maxWaveWidth: 120,
			waveLabelWidth: 60,
			stackedMinWidth: 120,
			stackedMinWidthCompact: 80,
			compactThreshold: 12,
			ultraCompactBarThreshold: 3,
		},
		row: {
			minHeight: 42,
			maxHeight: 90,
			minHeightClustered: 20,
			minHeightStacked: 42,
			labelHeight: 12,
			autoStretchThreshold: 3,
		},
		bar: {
			minHeight: 24,
			maxHeight: 32,
			maxWidth: 50,
			defaultWidth: "70%",
		},
		chart: {
			minHeight: 250,
			maxHeight: 900,
			defaultHeight: 300,
		},
	},
	typography: {
		charWidth: 7,
		fontSize: {
			label: 10,
			dataLabel: 10,
			dataLabelCompact: 9,
		},
	},
	spacing: {
		gap: {
			bar: 6,
			row: 8,
			rowSmall: 6,
			rowLarge: 6,
		},
		padding: {
			cell: {
				vertical: 4,
				horizontal: 4,
			},
			container: 24,
			buffer: 15,
			safety: 10,
		},
		bias: {
			stretch: 6,
			zoom: 8,
			zoomGapRecovery: 20,
		},
	},
	responsive: {
		breakpoints: {
			mobile: 768,
			tablet: 1024,
		},
		scales: {
			mobile: 0.7,
			tablet: 0.85,
			desktop: 1.0,
		},
	},
	charts: {
		horizontalClustered: {
			yAxisLabel: {
				minWidth: 120,
				maxWidth: 220,
				multiTileMinWidth: 70, // ← NEW — floor for multi-tile
				multiTileMaxWidth: 100,
			},
			rowThreshold: 5,
			diffThreshold: 20,
			statMarkerLayout: {
				gridPadding: 24, // extra grid.right for marker text
				minSegmentWidth: 0,
			},
		},
		horizontalStacked: {
			minLabelWidth: 12,
			barWidthApprox: 280,
			barWidthMultiplier: 0.45,
			maxBarWidth: 30,
			showLabelThreshold: 9,
			multiTileMinLabelWidth: 70, // ← NEW — floor for multi-tile
			multiTileMaxLabelWidth: 100,
			statMarkerLayout: {
				gridPadding: 0, // markers inside the bar, no extra padding needed
				minSegmentWidth: 30, // need at least 30px segment to show markers
			},
		},
		verticalClustered: {
			clusterBarWidth: 24,
			clusterBarWidthCompact: 20,
			clusterBarWidthUltraCompact: 20,
			barGap: 0,
			maxBarWidth: 35,
			maxBarWidthSingle: 40,
			minBarWidth: 12,
			singleBarWidthPercent: 0.6,
			statMarkerLayout: {
				gridPadding: 18, // extra grid.top for marker above bar
				minSegmentWidth: 0,
			},
		},
		verticalStacked: {
			minColumnWidth: 52,
			minColumnWidthCompact: 42,
			barWidthPercent: 0.65,
			minBarWidth: 10,
			maxBarWidth: 50,
			statMarkerLayout: {
				gridPadding: 16, // extra grid.right for marker beside stack
				minSegmentWidth: 18, // need minimal segment height
			},
		},
		horizontalLine: {
			minColumnWidth: 50,
			minColumnWidthCompact: 50,
			chartHeight: { min: 200, max: 600, default: 350 },
			lineWeight: 1,
			markerSize: 8,
			labelTopPadding: 30,
			attributesRowHeight: 28,
			axisTop: 25,
			axisBottom: 6,
			statMarkerLayout: {
				gridPadding: 0, // markers offset from data point, no grid change
				minSegmentWidth: 0,
			},
		},
		verticalLine: {
			minRowHeight: 40,
			minRowHeightCompact: 32,
			chartWidth: { min: 300, max: 800, default: 400 },
			lineWeight: 1,
			markerSize: 8,
			labelRightPadding: 35,
			attributesColWidth: 120,
			multiTileMinLabelWidth: 70, // ← NEW — floor for multi-tile
			multiTileMaxLabelWidth: 100,
			axisRight: 38,
			axisLeft: 20,
			xAxisScaleRight: 5,
			statMarkerLayout: {
				gridPadding: 0,
				minSegmentWidth: 0,
			},
			yAxisLabel: { minWidth: 120, maxWidth: 220, multiTileMaxWidth: 100 },
		},
		mini: {
			horizontal: {
				barGapSingle: "30%",
				barGapMultiple: "0%",
				barCategoryGapSingle: "40%",
				barCategoryGapMultiple: "0%",
			},
			vertical: {
				barWidth: "70%",
				barMaxWidth: 50,
			},
		},
	},
	tooltip: {
		backgroundColor: "#ffffff",
		borderColor: "#D6DFED",
		borderWidth: 1,
		borderRadius: "8px",
		fontSize: 12,
		lowNSizeThreshold: 30,
		lowNSizeColor: "#DC2626",
		textColor: "#1F2937",
		zIndex: 9999,
		boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
		displayTransition: false,
		transitionDuration: 0.05,
	},
	colors: {
		borders: {
			solid: "#D6DFED", // Updated from #E5E7EB - Figma blue-gray
			dashed: "#C5D0E3", // Updated from #D1D5DB - darker for dashed
			gray: "#D6DFED", // Updated from #D1D5DB
			lightGray: "#E8EDF5", // Updated from #F3F4F6 - lighter variant
		},
		backgrounds: {
			white: "#ffffff",
			alternate: "#F1F5F9",
		},
		text: {
			primary: "#403C45",
			secondary: "#63646B",
			tertiary: "#2F4B7F",
			quaternary: "#5E80B7",
			muted: "#6B7280",
		},
	},
	multiTile: multiTileScalingDefaults,
};

export const SINGLE_CHART_DEFAULTS: YAxisWidthConfig = {
	floorPx: 75,
	maxContainerFraction: 0.40,
	paddingPx: 32, // px-4 each side = 16*2
	minContentPx: 75,
	ceilingPx: 275,
	expansionFactor: 1.25,
};

// Defaults for multi-tile mode
export const MULTI_TILE_DEFAULTS: YAxisWidthConfig = {
	floorPx: 88,
	maxContainerFraction: 0.35,
	paddingPx: 12, // px-1 each side = 4*2 + 4 safety
	minContentPx: 88,
	ceilingPx: 150,
	expansionFactor: 1.25,
};

/**
 * Configuration manager class
 * Allows loading config from database or using defaults
 */
export class ChartConfigManager {
	private static instance: ChartConfigManager;
	private config: ChartConfiguration;

	private constructor(config?: ChartConfiguration) {
		this.config = config || defaultChartConfig;
	}

	static getInstance(config?: ChartConfiguration): ChartConfigManager {
		if (!ChartConfigManager.instance) {
			ChartConfigManager.instance = new ChartConfigManager(config);
		}
		return ChartConfigManager.instance;
	}

	/**
	 * Load configuration from database or API
	 */
	async loadFromDatabase(configId?: string): Promise<void> {
		try {
			// TODO: Implement actual database fetch
			// const response = await fetch(`/api/chart-config/${configId || 'default'}`);
			// const dbConfig = await response.json();
			// this.config = { ...defaultChartConfig, ...dbConfig };

			// For now, use default config
			this.config = defaultChartConfig;
		} catch (error) {
			console.error("Failed to load config from database, using defaults", error);
			this.config = defaultChartConfig;
		}
	}

	/**
	 * Update configuration at runtime
	 */
	updateConfig(partialConfig: Partial<ChartConfiguration>): void {
		this.config = { ...this.config, ...partialConfig };
	}

	/**
	 * Get full configuration
	 */
	getConfig(): ChartConfiguration {
		return this.config;
	}

	/**
	 * Get specific configuration section
	 */
	getDomainConfig(): DomainConfig {
		return this.config.domain;
	}

	getLayoutConfig(): LayoutConfig {
		return this.config.layout;
	}

	getDimensionsConfig(): DimensionsConfig {
		return this.config.dimensions;
	}

	getTypographyConfig(): TypographyConfig {
		return this.config.typography;
	}

	getSpacingConfig(): SpacingConfig {
		return this.config.spacing;
	}

	getResponsiveConfig(): ResponsiveConfig {
		return this.config.responsive;
	}

	getChartsConfig(): ChartsConfig {
		return this.config.charts;
	}

	/**
	 * Get chart-specific configuration
	 */
	getHorizontalClusteredConfig(): HorizontalClusteredConfig {
		return this.config.charts.horizontalClustered;
	}

	getHorizontalStackedConfig(): HorizontalStackedConfig {
		return this.config.charts.horizontalStacked;
	}

	getVerticalClusteredConfig(): VerticalClusteredConfig {
		return this.config.charts.verticalClustered;
	}

	getVerticalStackedConfig(): VerticalStackedConfig {
		return this.config.charts.verticalStacked;
	}

	getHorizontalLineConfig(): HorizontalLineConfig {
		return this.config.charts.horizontalLine;
	}

	getVerticalLineConfig(): VerticalLineConfig {
		return this.config.charts.verticalLine;
	}

	getMiniChartConfig(): MiniChartConfig {
		return this.config.charts.mini;
	}

	getStatMarkerConfig(): IStatMarkerConfig {
		return this.config.charts.statMarker;
	}

	/**
	 * Get tooltip configuration
	 */
	getTooltipConfig(): TooltipConfig {
		return this.config.tooltip;
	}

	/**
	 * Get colors configuration
	 */
	getColorsConfig(): ColorsConfig {
		return this.config.colors;
	}

	getMultiTileScalingConfig(): MultiTileScalingConfig {
		return this.config.multiTile;
	}
}

/**
 * Hook for using chart configuration in React components
 */
export const useChartConfig = () => {
	return ChartConfigManager.getInstance().getConfig();
};

/**
 * Export singleton instance for direct access
 */
export const chartConfig = ChartConfigManager.getInstance();
